import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, AlertTriangle, Info, ArrowRight, Upload, Check, X, HelpCircle, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = { critica: "Crítica", importante: "Importante", mejora: "Mejora" };
const CAT_COLOR = { critica: "bg-[#EF4444]", importante: "bg-[#F59E0B]", mejora: "bg-[#64748B]" };

function OpcionBtn({ active, tone, onClick, testid, icon: Icon, children }) {
  const base = "flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold border-2 transition-colors";
  const on = { si: "bg-[#10B981] border-[#10B981] text-white", no: "bg-[#EF4444] border-[#EF4444] text-white", nose: "bg-white/15 border-white/30 text-white" }[tone];
  const off = "bg-white/5 border-white/10 text-white/60 hover:border-white/30";
  return (
    <button onClick={onClick} data-testid={testid} className={`${base} ${active ? on : off}`}>
      <Icon size={14} />{children}
    </button>
  );
}

export default function DiagnosticoRapido() {
  const { reload } = useAuth();
  const navigate = useNavigate();
  const [preguntas, setPreguntas] = useState([]);
  const [aviso, setAviso] = useState("Resultado orientativo. Sube tus documentos para el análisis real y validado.");
  const [resp, setResp] = useState({});
  const [resultado, setResultado] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/diagnostico-rapido/preguntas")
      .then((r) => { setPreguntas(r.data.preguntas); if (r.data.aviso) setAviso(r.data.aviso); })
      .catch(() => {});
  }, []);

  const set = (id, val) => setResp((r) => ({ ...r, [id]: val }));
  const contestadas = Object.keys(resp).length;

  const ver = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/diagnostico-rapido", { respuestas: resp });
      setResultado(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error("No se pudo calcular el diagnóstico"); }
    setBusy(false);
  };

  const salir = async (destino) => {
    try { await api.post("/diagnostico-rapido/hecho"); await reload?.(); } catch (e) { /* seguimos igualmente */ }
    navigate(destino);
  };

  const readinessColor = (r, topado) =>
    topado || r < 40 ? "text-[#EF4444]" : r >= 70 ? "text-[#10B981]" : "text-[#F59E0B]";

  return (
    <div className="min-h-screen bg-[#0E2A47] px-4 py-8" data-testid="diagnostico-rapido">
      <div className="w-full max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="text-[#FF5625]" size={26} />
          <span className="font-black text-lg tracking-tighter text-white">AuditReady<span className="text-[#FF5625]">.</span></span>
        </div>

        {/* Banner ORIENTATIVO — grande e imposible de no ver */}
        <div className="rounded-xl bg-[#FF5625]/15 border-2 border-[#FF5625] px-5 py-4 mb-8 flex items-start gap-3" data-testid="diag-banner">
          <Info className="text-[#FF5625] shrink-0 mt-0.5" size={22} />
          <p className="text-white font-semibold text-base leading-snug">{aviso}</p>
        </div>

        {!resultado ? (
          <>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF5625] mb-2">
              <Sparkles size={14} /> Diagnóstico rápido · 2 minutos
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white leading-tight">
              ¿Cómo de preparado estás? Responde 15 preguntas
            </h1>
            <p className="mt-2 text-white/60 mb-6">Sin subir nada. Te damos un readiness <b className="text-white">estimado</b> y qué resolver primero.</p>

            <div className="space-y-3">
              {preguntas.map((q, i) => (
                <div key={q.id} className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid={`diag-pregunta-${q.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="text-white text-sm font-medium leading-snug">
                      <span className="text-white/40 mr-1">{i + 1}.</span>{q.texto}
                    </div>
                    <Badge className={`${CAT_COLOR[q.categoria]} hover:${CAT_COLOR[q.categoria]} shrink-0 text-[10px]`}>{CAT_LABEL[q.categoria]}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <OpcionBtn active={resp[q.id] === "si"} tone="si" onClick={() => set(q.id, "si")} testid={`diag-${q.id}-si`} icon={Check}>Sí</OpcionBtn>
                    <OpcionBtn active={resp[q.id] === "no"} tone="no" onClick={() => set(q.id, "no")} testid={`diag-${q.id}-no`} icon={X}>No</OpcionBtn>
                    <OpcionBtn active={resp[q.id] === "nose"} tone="nose" onClick={() => set(q.id, "nose")} testid={`diag-${q.id}-nose`} icon={HelpCircle}>No lo sé</OpcionBtn>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Button onClick={ver} disabled={busy || contestadas === 0} data-testid="diag-ver-resultado"
                className="bg-[#FF5625] hover:bg-[#E0451A] text-white font-semibold px-8">
                {busy ? <><Loader2 size={16} className="mr-1 animate-spin" />Calculando…</> : `Ver mi resultado (${contestadas}/${preguntas.length})`}
              </Button>
              <button onClick={() => salir("/subir-ia")} data-testid="diag-saltar" className="text-sm text-white/50 hover:text-white/80">
                Saltar y subir documentos
              </button>
            </div>
          </>
        ) : (
          <div data-testid="diag-resultado">
            <div className="rounded-2xl bg-white p-6 sm:p-8 mb-6 text-center">
              <div className="text-xs uppercase tracking-[0.3em] text-[#0E2A47]/50 mb-2">Readiness estimado</div>
              <div className={`text-7xl font-black tracking-tighter ${readinessColor(resultado.readiness_estimado, resultado.topado)}`} data-testid="diag-readiness">
                {resultado.readiness_estimado}%
              </div>
              {resultado.topado && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#EF4444]/10 text-[#EF4444] px-4 py-2 text-sm font-semibold" data-testid="diag-tope">
                  <AlertTriangle size={16} /> Limitado al 40%: tienes un requisito crítico sin cubrir. Resuélvelo primero.
                </div>
              )}
            </div>

            {resultado.resuelve_ya?.length > 0 && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-5 mb-4" data-testid="diag-resuelve-ya">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-[#FF5625]" /> Resuelve ya</h3>
                <div className="space-y-2">
                  {resultado.resuelve_ya.map((it) => (
                    <div key={it.id} className="flex items-start gap-3 rounded-lg bg-white/5 p-3" data-testid={`diag-resolver-${it.id}`}>
                      <Badge className={`${CAT_COLOR[it.categoria]} hover:${CAT_COLOR[it.categoria]} shrink-0 text-[10px] mt-0.5`}>{CAT_LABEL[it.categoria]}</Badge>
                      <div className="min-w-0">
                        <div className="text-white text-sm">{it.texto}</div>
                        <div className="text-white/40 text-xs font-mono mt-0.5">Requisitos: {it.requisitos.join(", ")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resultado.revisar?.length > 0 && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-5 mb-6" data-testid="diag-revisar">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2"><HelpCircle size={16} className="text-[#F59E0B]" /> Revísalo (no lo sabías)</h3>
                <div className="space-y-1.5">
                  {resultado.revisar.map((it) => (
                    <div key={it.id} className="text-white/70 text-sm flex items-center gap-2" data-testid={`diag-revisar-${it.id}`}>
                      <span className="text-[#F59E0B]">•</span>{it.texto}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl bg-[#FF5625] p-5 flex flex-wrap items-center gap-4" data-testid="diag-cta-card">
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold">Siguiente paso: sube tu Plan APPCC</div>
                <div className="text-white/80 text-sm">Es el documento que más cobertura te da. Empieza por ahí para el análisis real.</div>
              </div>
              <Button onClick={() => salir("/subir-ia")} data-testid="diag-cta-appcc" className="bg-white text-[#0E2A47] hover:bg-white/90 font-semibold shrink-0">
                <Upload size={16} className="mr-1" /> Subir mi Plan APPCC <ArrowRight size={15} className="ml-1" />
              </Button>
            </div>

            <div className="mt-5 text-center">
              <button onClick={() => salir("/")} data-testid="diag-ir-panel" className="text-sm text-white/50 hover:text-white/80">
                Ir a mi panel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
